import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText
} from "@mui/material";
import {
  debounce
} from 'lodash';
import { KEY__CREDENTIAL__PRIMARY } from '../../../dictionaries/Credential.dic';
import {
  STAGE_CONFIRMATION,
  STAGE_SUBMISSION
} from '../../../dictionaries/Engagement.dic';
import Ui3DotsMenu from '../../../dictionaries/Ui3DotsMenu.dic';
import AccountLib from '../../../lib/Account';
import {
  Arr,
  join,
  sanitizeArr
} from '../../../lib/Array.lib';
import {
  NOT,
  YES
} from '../../../lib/Boolean.lib';
import Core from '../../../lib/Core';
import {
  REC_MSG_TYPE__REJECTION_ID,
  STATE_ACTIVE
} from '../../../lib/Definition';
import fetchEmployerName from '../../../lib/Employer/fetchEmployerName.async';
import {
  EngagementLib
} from '../../../lib/Engagement';
import globalErrorHandler from '../../../lib/Error/globalErrorHandler.fun';
import useState from '../../../lib/hooks/useState.hook';
import {
  Obj
} from '../../../lib/Object.lib';
import {
  COLLECTION__EMPLOYER_SOURCE_LISTS,
  COLLECTION__JOBS,
  readLoopbackRecord
} from '../../../lib/services/BE/loopback.api';
import {
  combineEmailsLists,
  mapEmailsListToRecipients,
  mapEmailsListToStringArray,
  sendSafeEmail
} from '../../../lib/services/Email/Email.lib';
import Streak from '../../../lib/Streak';
import {
  joinKeyName
} from '../../../lib/String.lib';
import {
  getJobSourceByJobId
} from '../../../lib/tools/getJobSourceListApis';
import EmailPreview from '../../Dialogs/EmailPreview';
import Box from '../../Layout/Wrappers/Box';
import Button from '../../Layout/Wrappers/Button';
import Checkbox from '../../Layout/Wrappers/Checkbox';
import DrawerBar from '../../Layout/Wrappers/DrawerBar';
import Fieldset from '../../Layout/Wrappers/Fieldset';
import IconButton from '../../Layout/Wrappers/IconButton';
import Message from '../../Layout/Wrappers/Message';
import Once from '../../Layout/Wrappers/Once';
import ActionBarNotifyRecruiters from './ActionBarNotifyRecruiters';

const DATA__RULE__VALID_STAGES = [
  STAGE_SUBMISSION,
  STAGE_CONFIRMATION
];

export default function PreviewRecruiterEmployerUpdates({ employerId, snippets }) {
  const [state, updateState] = useState();

  const DATA__COLLECTION = Arr(state.collection);
  const DATA__SELECTED = Arr(state.selected);
  const HASH__RECIPIENTS = Obj(state.recipients);

  const YES__COLLECTION = YES(state.collection);
  const YES__LENGTH__DATA__COLLECTION = YES(DATA__COLLECTION.length);
  const YES__CHECKED__ALL = DATA__SELECTED.length === DATA__COLLECTION.length;

  const NOT__COLLECTION = NOT(YES__COLLECTION);
  const NOT__LENGTH__DATA__COLLECTION = NOT(YES__LENGTH__DATA__COLLECTION);

  const _fetchData = async ({
    notifiedRecruiters = [],
  } = {}) => {
    try {
      Core.showLoader('Loading recruiters...');
      const recruiterHash = {};
      const recipients = {};
      const recruiters = [];

      fetchEmployerName({ employerId }).then(employerName => {
        updateState({
          employerName,
        });
      });

      // ==========================
      // STEP 1: Get employer's jobs
      // ==========================
      const jobs = Arr(await readLoopbackRecord({
        collection: COLLECTION__JOBS,
        where: { employerId, state: STATE_ACTIVE },
        fields: ['id'],
      }));
      const jobIds = sanitizeArr(jobs.map(j => j.id));
      if (!jobIds.length) {
        Core.showError('No jobs found for this employer.');
        return;
      }

      // ==========================
      // STEP 2: Collect sourcing recruiters
      // ==========================
      const employerSourceLists = Arr(
        await readLoopbackRecord({
          collection: COLLECTION__EMPLOYER_SOURCE_LISTS,
          where: { employerId, endDate: { eq: null } },
          mapper: (records) => records.map(record => ({
            ...record,
            YES__EMPLOYER_SOURCE_LIST: true,
          }))
        })
      );

      const jobSources = [];
      for (const jobId of jobIds) {
        jobSources.push(...Arr(await getJobSourceByJobId({ jobId })).map(
          record => ({ ...record, YES__JOB_SOURCE_LIST: true })
        ));
      }

      const allSourceList = [...jobSources, ...employerSourceLists];
      const sourceListIds = sanitizeArr(allSourceList.map(({ accountId }) => accountId));

      if (sourceListIds.length) {
        recruiters.push(...Arr(await AccountLib.getWhere({
          id: { inq: sourceListIds }
        })));
      }

      const __mapRecipients = ({ recruiter, candidate }) => {
        const recruiterRecipients = mapEmailsListToRecipients({
          emailsList: recruiter.emailsList,
          primaryContact: { name: recruiter._name, email: recruiter.email, accountType: 'Recruiter Main Contact' },
          accountType: 'Recruiter Additional Contact',
          messageTypes: [REC_MSG_TYPE__REJECTION_ID]
        });
        const candidateRecipients = mapEmailsListToRecipients({
          emailsList: candidate ? candidate.emailsList : [],
          accountType: 'Candidate Recruiter Contact',
          messageTypes: [REC_MSG_TYPE__REJECTION_ID]
        });
        recruiterRecipients.emails = combineEmailsLists(candidateRecipients.emails, recruiterRecipients.emails);
        recruiterRecipients.to = combineEmailsLists(candidateRecipients.to, recruiterRecipients.to);
        recruiterRecipients.cc = combineEmailsLists(candidateRecipients.cc, recruiterRecipients.cc);
        recruiterRecipients.bcc = combineEmailsLists(candidateRecipients.bcc, recruiterRecipients.bcc);
        recipients[recruiter.id] = recruiterRecipients;
        recipients[recruiter.id]._to = mapEmailsListToStringArray(recipients[recruiter.id].to);
      };

      recruiters.forEach(recruiter => {
        if (recruiter.state !== STATE_ACTIVE) return;
        recruiterHash[recruiter.id] = { id: recruiter.id, recruiter, YES__SOURCE_LIST: true };
        __mapRecipients({ recruiter });
      });

      // ==========================
      // STEP 3: Collect recruiters via engagements
      // ==========================
      EngagementLib.cleanCache();
      for (const jobId of jobIds) {
        const engagements = Arr(await EngagementLib.getWhere({ jobId }));
        engagements
          .filter(e =>
            e.submitted ||
            (DATA__RULE__VALID_STAGES.includes(e.stage) && !!e.matchedByWho)
          )
          .forEach(({ recruiter = {}, candidate = {} }) => {
            if (!recruiter.id) return;
            if (!recruiterHash[recruiter.id]) {
              recruiterHash[recruiter.id] = { id: recruiter.id, recruiter, candidate, YES__ENGAGEMENT: true };
            }
            recruiterHash[recruiter.id].DATA__CANDIDATE_NAMES = Arr(recruiterHash[recruiter.id].DATA__CANDIDATE_NAMES);
            recruiterHash[recruiter.id].DATA__CANDIDATE_NAMES.push(candidate._name);
            __mapRecipients({ recruiter, candidate });
          });
      }

      // ==========================
      // STEP 4: Exclude already notified recruiters
      // ==========================
      const hashRecruitersToExclude = {};
      notifiedRecruiters.forEach(id => { hashRecruitersToExclude[id] = true; });

      const collection = Object.values(recruiterHash).filter(
        ({ recruiter }) => NOT(hashRecruitersToExclude[recruiter.id])
      );

      await updateState({ collection, recipients });
      Core.hideMessage();
    } catch (error) {
      globalErrorHandler(error);
    }
  };

  const _onCheckAll = () => {
    if (YES__CHECKED__ALL) updateState({ selected: [] });
    else updateState({ selected: DATA__COLLECTION.map(({ id }) => id) });
  };

  const _onCheck = (value) => () => {
    const selected = [...DATA__SELECTED];
    const index = selected.indexOf(value);
    if (index === -1) selected.push(value);
    else selected.splice(index, 1);
    updateState({ selected });
  };

  const _onSubmit = async () => {
    try {
      const emailParams = getControllerEmailPreviewRecruiterEmployerUpdates().getParams();
      if (!emailParams.subject) return Core.showError('Missing email subject.');
      if (!emailParams.html) return Core.showError('Missing email content.');

      const selectedItems = DATA__COLLECTION.filter(({ id }) => DATA__SELECTED.includes(id));
      Core.dialog.open({
        title: <>Action Summary</>,
        content: (
          <Fieldset title="Recruiters to notify">
            <ul>
              {selectedItems.map(({ recruiter }, index) => (
                <li key={joinKeyName(['recruiter_to_notify', recruiter.id, index])}>
                  <span>{HASH__RECIPIENTS[recruiter.id]._to.join(', ')}</span>
                </li>
              ))}
            </ul>
          </Fieldset>
        ),
        actions: [
          <Button outlined>Cancel</Button>,
          <Button primary onClick={async () => {
            Core.showLoader('Sending emails...');
            const notifiedIds = [];
            await Promise.allSettled(selectedItems.map(async ({ id }) => {
              const emailData = {
                ...emailParams,
                to: join(HASH__RECIPIENTS[id]._to),
                source: 'PreviewRecruiterEmployerUpdates__onSubmit__Confirm',
                key: KEY__CREDENTIAL__PRIMARY,
              };
              await sendSafeEmail(emailData);
              notifiedIds.push(id);
              return true;
            }));
            // Re-fetch data excluding notified recruiters
            await _fetchData({ notifiedRecruiters: notifiedIds });
          }}>
            Confirm
          </Button>
        ]
      });
    } catch (error) {
      globalErrorHandler(error);
    }
  };

  setControllerPreviewRecruiterEmployerUpdates({
    refetch: debounce(_fetchData),
  });

  return (
    <Box column flex1>
      <Once condition={NOT__COLLECTION} promise={_fetchData} />
      <Message show={NOT__LENGTH__DATA__COLLECTION}>
        There are no recruiters linked to this employer (sourcing or submissions)
      </Message>
      <Box column flex1 acl={YES__LENGTH__DATA__COLLECTION}>
        {YES__COLLECTION && (
          <Fieldset
            title="Notify recruiters with past submissions or actively sourcing for employer's jobs"
            subtitle={state.employerName}
            nextToTitle={
              <IconButton
                icon="refresh"
                title="Refetch data"
                onClick={_fetchData}
              />
            }
            wrapperProps={{ className: 'm-0' }}
          >
            <Box column flex1
              style={{
                height: 'calc(100vh - 111px)'
              }}
            >
              <Box column flex1
                className='scroll-y scroll-y-visible'
                style={{
                  minHeight: '90px'
                }}
              >
                <List>
                  <ListItem>
                    <ListItemButton dense onClick={_onCheckAll}>
                      <ListItemIcon>
                        <Checkbox checked={YES__CHECKED__ALL} className='icon24' />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <strong>
                            All ({DATA__COLLECTION.length || 0})
                          </strong>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  {DATA__COLLECTION.map(({
                    id,
                    recruiter,
                    DATA__CANDIDATE_NAMES = [],
                    YES__SOURCE_LIST
                  }) => {
                    const checked = DATA__SELECTED.includes(id);
                    return (
                      <ListItem key={id}>
                        <ListItemButton dense onClick={_onCheck(id)}>
                          <ListItemIcon>
                            <Checkbox checked={checked} className='icon24' />
                          </ListItemIcon>
                          <ListItemText
                            className="truncate"
                            primary={
                              <>
                                <span>{recruiter._name}</span>
                                {YES__SOURCE_LIST && (
                                  <>
                                    &nbsp;-&nbsp;
                                    <span className='c-red'>
                                      (currently sourcing for this employer)
                                    </span>
                                  </>
                                )}
                                {!!DATA__CANDIDATE_NAMES.length && (
                                  <>
                                    &nbsp;-&nbsp;
                                    <span className='c-black-medium f-sm'>
                                      {join(DATA__CANDIDATE_NAMES)}
                                    </span>
                                  </>
                                )}
                              </>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
              <Box column className='border-top'>
                <Box column className="px-2" style={{ maxHeight: '50vh' }}>
                  <EmailPreview taller omitUIRecipients
                    ref={setControllerEmailPreviewRecruiterEmployerUpdates}
                    snippets={snippets}
                  />
                </Box>
                <ActionBarNotifyRecruiters noSubOptions
                  selected={DATA__SELECTED}
                  onSubmit={_onSubmit}
                />
              </Box>
            </Box>
          </Fieldset>
        )}
      </Box>
    </Box>
  );
}

// ===============================
// CONTROLLERS
// ===============================
const KEY__CONTROLLER__RECRUITER_EMPLOYER_UPDATES = 'RecruiterEmployerNotificationPreviewRef';
const KEY__CONTROLLER__EMAIL_PREVIEW__RECRUITER_EMPLOYER_UPDATES = 'EmailPreviewRecruiterEmployerUpdates';

export function setControllerPreviewRecruiterEmployerUpdates(controller) {
  Core.setKeyValue(KEY__CONTROLLER__RECRUITER_EMPLOYER_UPDATES, controller);
}

export function getControllerPreviewRecruiterEmployerUpdates() {
  return Obj(Core.getKeyValue(KEY__CONTROLLER__RECRUITER_EMPLOYER_UPDATES));
}

export function setControllerEmailPreviewRecruiterEmployerUpdates(controller) {
  Core.setKeyValue(KEY__CONTROLLER__EMAIL_PREVIEW__RECRUITER_EMPLOYER_UPDATES, controller);
}

export function getControllerEmailPreviewRecruiterEmployerUpdates() {
  return Obj(Core.getKeyValue(KEY__CONTROLLER__EMAIL_PREVIEW__RECRUITER_EMPLOYER_UPDATES));
}

export async function openPreviewRecruiterEmployerUpdates({ employerId }) {
  Core.showLoader();
  const snippets = await Streak.getSnippets();
  Core.hideMessage();
  Core.openDrawer({
    style: {
      width: 1200,
      maxWidth: 'calc(100vw - 2rem)',
    },
    content: (
      <Box column>
        <DrawerBar
          title={Ui3DotsMenu.Employer.previewRecruiterEmployerUpdates}
        />
        <PreviewRecruiterEmployerUpdates employerId={employerId} snippets={snippets} />
      </Box>
    ),
  });
}
